/**
 * The design workspace (requirement 13.1): the design's workflow as steps,
 * from schematic capture through design checks to the later layers. Capture
 * is the palette, canvas and inspector; checks put the findings where the
 * palette was, beside the same canvas and inspector, so a finding is fixed
 * where it is found. Both work on one working document.
 *
 * This component owns the conversation with the server; the editor reducer
 * owns the document. The split is design §1.3's: editing is local and
 * immediate, and the draft is written through `PUT /draft` with the revision
 * this session last saw.
 *
 * - Saving is debounced and automatic. A save that the schema refuses is shown
 *   as findings and not retried until the document changes again.
 * - A conflict (requirement 13.6, A4) is someone else's change, not a failure.
 *   Local work stays on screen and unsaved; the designer chooses between saving
 *   over theirs or adopting theirs, and adopting keeps local work as an undo
 *   step. Nothing is merged.
 * - Checking runs on demand (design §11), from the Design checks step, and
 *   the panel there says how current it is.
 */
import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../auth";
import {
  ApiError,
  api,
  type BlockUpdate,
  type CheckReport,
  type Design,
  type DisplayUnitsBody,
  type Draft,
  type LibraryEntity,
  type UpdatedBlock,
} from "../api/client";
import { DisplayUnitsContext } from "./DisplayUnitsContext";
import { PaneResizer } from "./PaneResizer";
import { inheritedUnits, UnitsPicker } from "./UnitsPicker";
import { liquidUnits, type DisplayUnitsSetting } from "./units";
import { Banner, ConfirmDialog, NOTICE_DISMISS_MS } from "../components";
import type { DesignDocument, DesignKind, Finding } from "../design/document";
import {
  createEditorState,
  editorReducer,
  ContextMenu,
  EditorCanvas,
  fitToBounds,
  hasUnsavedChanges,
  lockingInstance,
  placeFromPalette,
  sceneViewFor,
  viewportCentre,
  zoomAbout,
  ZOOM_STEP,
  type EditorHost,
} from "../editor";
import { buildScene, DEFAULT_VIEW } from "../scene";
import { describeChanges, elementChanges, type ElementChanges } from "./conflict";
import { FindingsPanel } from "./FindingsPanel";
import {
  FindingsToggle,
  ScriptFindings,
  scriptMenu,
  ScriptsCanvasEditor,
  ScriptsEditor,
  ScriptsPanel,
  StepNavigator,
  useScripts,
} from "./Scripts";
import { Inspector, type BlockActions } from "./Inspector";
import {
  EMPTY_WORKSPACE_LIBRARY,
  extendLibrary,
  latestAirLine,
  latestConnector,
  latestJunction,
  profilesForKind,
  swapCandidates,
  loadWorkspaceLibrary,
  reloadBlocks,
  type WorkspaceLibrary,
} from "./library";
import { Palette } from "./Palette";
import { locateSchemaFinding } from "./parameters";
import { EditorTools } from "./EditorTools";
import { PanelTab } from "./PanelTab";
import "./workspace.css";
import {
  loadAnnotations,
  loadUserDefaults,
  loadGrid,
  loadPaneOpen,
  loadPaneWidths,
  PANES,
  saveAnnotations,
  saveGrid,
  savePaneOpen,
  savePaneWidths,
  saveUserDefaults,
  type FoldablePane,
  type Pane,
} from "./preferences";
import {
  AirLinesIcon,
  DefaultsIcon,
  DimensionsIcon,
  ExportIcon,
  FluidLinesIcon,
  NotesIcon,
  InsideIcon,
  LabelsIcon,
  OutsideIcon,
  ZoomExtentsIcon,
} from "../icons";
import { exportStill, pdfAvailability, type DrawScene, type StillFormat } from "../render/still";
import type { Point, SceneAnnotations } from "../scene/types";
import { definitionCentre, describeUpdate, errorText, SaveBlockDialog } from "./blocks";
import { DefaultsDialog } from "./DefaultsDialog";
import { StartFromDesignDialog } from "./StartFromDesignDialog";
import { bomTables, hasBillOfMaterials } from "./billOfMaterials";
import {
  ExportOptionsDialog,
  pdfChoices,
  pdfPagesChosen,
  svgChoices,
  type ExportChoiceGroup,
} from "./ExportOptionsDialog";
import type { StepPreview } from "../scripts/preview";
import { exportSteps, STEPS_EXPORTS, stepPages, stepSheets, stepsFilename, type StepsFormat } from "./stepsExport";

/** How long editing pauses before the draft is written. */
export const SAVE_DELAY_MS = 800;

export interface Conflict {
  theirs: Draft;
  changes: ElementChanges;
  /** Saved by this user elsewhere (another tab) rather than by a colleague. */
  byMe: boolean;
}

type SaveStatus = "idle" | "saving" | "failed";

export function useSize<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [size, setSize] = useState({ width: 900, height: 520 });
  // Only a real change is kept, so measuring often costs a comparison, not a render.
  const measure = useCallback(() => {
    const element = ref.current;
    if (!element) return;
    const width = Math.floor(element.clientWidth);
    const height = Math.floor(element.clientHeight);
    // A hidden box (a later workflow step) measures zero: keep the last real size.
    if (width <= 0 || height <= 0) return;
    setSize((current) => (current.width === width && current.height === height ? current : { width, height }));
  }, []);
  // After every render: a panel folding, a step changing, or a strip coming or
  // going resizes the box in the same commit, before a resize observer reports it.
  useLayoutEffect(measure);
  useEffect(() => {
    const element = ref.current;
    window.addEventListener("resize", measure);
    const observer = element && typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    if (element) observer?.observe(element);
    return () => {
      window.removeEventListener("resize", measure);
      observer?.disconnect();
    };
  }, [measure]);
  return [ref, size] as const;
}

function asDocument(content: Record<string, unknown>): DesignDocument {
  return content as DesignDocument;
}

// --- The header's pieces ------------------------------------------------------

/**
 * Whether the draft has reached the server. Quiet while things are working:
 * the draft saves itself, so "Saving…" and "Saved" are reassurance, not a call
 * to act. Only a failure or a conflict asks for attention.
 */
export function SaveState({ unsaved, status, conflict }: { unsaved: boolean; status: SaveStatus; conflict: boolean }) {
  if (conflict || status === "failed") {
    return (
      <span className="ws-pill ws-pill-danger" role="status">
        {conflict ? "Not saved · changed elsewhere" : "Not saved"}
      </span>
    );
  }
  return (
    <span className="ws-save-state" role="status">
      {status === "saving" || unsaved ? "Saving…" : "Saved"}
    </span>
  );
}

/**
 * The asterisk after the design's name: this draft differs from its latest
 * published version, or has never been published. Publishing is the deliberate
 * act (D29), so this is what the header draws attention to.
 */
function UnpublishedMark({ unpublished, latestVersion }: { unpublished: boolean; latestVersion: number | null }) {
  if (!unpublished) return null;
  const meaning = latestVersion === null ? "not yet published" : `unpublished changes since v${latestVersion}`;
  return (
    <span className="ws-dirty" title={meaning.charAt(0).toUpperCase() + meaning.slice(1)}>
      <span aria-hidden="true">*</span>
      <span className="visually-hidden">{`(${meaning})`}</span>
    </span>
  );
}

/**
 * The design's name, renamed in place: click it (or focus it and press Enter),
 * type, and Enter or leaving the field keeps the new name; Escape keeps the
 * old one. The name belongs to the design, not to a version (invariant 1), so
 * renaming changes no published version and needs no publish.
 */
function DesignName({
  name,
  disabled,
  onRename,
}: {
  name: string;
  disabled: boolean;
  /** Resolves true when the server took the new name. */
  onRename: (name: string) => Promise<boolean>;
}) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(name);
  const [saving, setSaving] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const done = useRef(false);

  const start = () => {
    setValue(name);
    done.current = false;
    setEditing(true);
  };
  const finish = async (keep: boolean) => {
    // Enter then blur, or Escape then blur, finish once.
    if (done.current) return;
    done.current = true;
    const next = value.trim();
    if (keep && next && next !== name) {
      setSaving(true);
      const renamed = await onRename(next);
      setSaving(false);
      if (!renamed) {
        // Left open with what was typed, so it can be corrected or abandoned.
        done.current = false;
        return;
      }
    }
    setEditing(false);
    window.setTimeout(() => buttonRef.current?.focus(), 0);
  };

  if (editing) {
    return (
      <input
        className="ws-name-input"
        aria-label="Design name"
        value={value}
        maxLength={200}
        disabled={saving}
        autoFocus
        onFocus={(event) => event.currentTarget.select()}
        onChange={(event) => setValue(event.target.value)}
        onBlur={() => void finish(true)}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            event.preventDefault();
            void finish(true);
          } else if (event.key === "Escape") {
            event.preventDefault();
            void finish(false);
          }
        }}
      />
    );
  }
  return (
    <button
      ref={buttonRef}
      type="button"
      className="ws-name-button"
      title={`${name} (click to rename)`}
      aria-label={`Rename design ${name}`}
      disabled={disabled}
      onClick={start}
    >
      {name}
    </button>
  );
}

/**
 * The design workflow, in the order of the layers in the architecture (§1),
 * with checking against the manufacturing profile as its own step between
 * capture and scripting. Only the first two are built; the rest are named so
 * the order is visible, and their panels say when they arrive. Placement, routing and CAD
 * are Phase 2: these tabs are labels, not a start on that work.
 */
export const WORKFLOW_STEPS = [
  { id: "schematic", label: "Schematic" },
  { id: "checks", label: "Design checks" },
  { id: "scripts", label: "Workflows" },
  {
    id: "simulation",
    label: "Simulation",
    phase: 1,
    about: "Running a script through a solver plugin, then playing the recording back step by step.",
  },
  {
    id: "routing",
    label: "Layout",
    phase: 2,
    about: "Placing components and routing channels onto the manufacturing profile's layers.",
  },
  {
    id: "cad",
    label: "CAD",
    phase: 2,
    about: "Generating parametric CAD from the placed and routed design.",
  },
] as const;

export type WorkflowStep = (typeof WORKFLOW_STEPS)[number]["id"];

/**
 * The steps as tabs. The selected tab carries the tint of a pressed canvas
 * control, drawn on the tab itself so it always covers the whole label.
 */
function WorkflowSteps({
  step,
  onStep,
  errors,
  panelId,
}: {
  step: WorkflowStep;
  onStep: (step: WorkflowStep) => void;
  /** Errors from the last check, or a refused save, shown on the checks tab. */
  errors: number;
  panelId: string;
}) {
  const tabs = useRef(new Map<WorkflowStep, HTMLButtonElement>());
  // Arrow keys move along the tabs and select as they go (WAI-ARIA tabs).
  const move = (event: ReactKeyboardEvent, index: number) => {
    const last = WORKFLOW_STEPS.length - 1;
    const next =
      event.key === "ArrowRight" ? (index === last ? 0 : index + 1)
      : event.key === "ArrowLeft" ? (index === 0 ? last : index - 1)
      : event.key === "Home" ? 0
      : event.key === "End" ? last
      : null;
    if (next === null) return;
    event.preventDefault();
    const id = WORKFLOW_STEPS[next]!.id;
    onStep(id);
    tabs.current.get(id)?.focus();
  };

  return (
    <div className="ws-steps" role="tablist" aria-label="Design workflow">
      {WORKFLOW_STEPS.map((candidate, index) => {
        const selected = candidate.id === step;
        return (
          <button
            key={candidate.id}
            ref={(element) => {
              if (element) tabs.current.set(candidate.id, element);
              else tabs.current.delete(candidate.id);
            }}
            type="button"
            role="tab"
            id={`${panelId}-tab-${candidate.id}`}
            className="ws-step"
            aria-selected={selected}
            aria-controls={panelId}
            tabIndex={selected ? 0 : -1}
            onClick={() => onStep(candidate.id)}
            onKeyDown={(event) => move(event, index)}
          >
            <span className="ws-step-number" aria-hidden="true">
              {index + 1}
            </span>
            {candidate.label}
            {candidate.id === "checks" && errors > 0 && (
              <span className="ws-step-badge" title={`${errors} error${errors === 1 ? "" : "s"}`}>
                <span aria-hidden="true">{errors}</span>
                <span className="visually-hidden">{`, ${errors} error${errors === 1 ? "" : "s"}`}</span>
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

function ProfilePicker({
  profiles,
  current,
  currentLabel,
  disabled,
  onChange,
}: {
  profiles: readonly LibraryEntity[];
  current: string | null;
  currentLabel: string | null;
  disabled: boolean;
  onChange: (versionId: string | null) => void;
}) {
  const offered = profiles.filter((profile) => profile.latest_version_id);
  const pinnedOlder = current !== null && !offered.some((profile) => profile.latest_version_id === current);
  return (
    <label className="ws-inline">
      Profile
      <select
        value={current ?? ""}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value || null)}
        aria-label="Manufacturing profile"
      >
        <option value="">Project default</option>
        {pinnedOlder && <option value={current}>{currentLabel ?? "Pinned version"}</option>}
        {offered.map((profile) => (
          <option key={profile.id} value={profile.latest_version_id!}>
            {profile.name} v{profile.latest_version_number}
          </option>
        ))}
      </select>
    </label>
  );
}

function ConflictBanner({
  conflict,
  busy,
  onKeepMine,
  onAdoptTheirs,
}: {
  conflict: Conflict;
  busy: boolean;
  onKeepMine: () => void;
  onAdoptTheirs: () => void;
}) {
  const when = new Date(conflict.theirs.updated_at).toLocaleTimeString();
  return (
    <div className="ws-conflict" role="alert">
      <div>
        <strong>
          {conflict.byMe ? "You saved this design from another window" : "Someone else saved this design"} at {when}
        </strong>{" "}
        (revision {conflict.theirs.revision}). Their save {describeChanges(conflict.changes)}.
      </div>
      <div>Your changes are still here and have not been saved. Nothing has been merged.</div>
      <div className="ws-row">
        <button type="button" onClick={onKeepMine} disabled={busy}>
          Save mine over theirs
        </button>
        <button type="button" onClick={onAdoptTheirs} disabled={busy}>
          Use theirs (mine stays in undo)
        </button>
      </div>
    </div>
  );
}

/**
 * Pan and zoom, floating over the top of the canvas. They change the view and
 * never the design (requirement 12.8), so they sit on the canvas rather than
 * among the editing tools.
 */
export function ViewControls({
  zoom,
  canFit,
  annotations,
  onZoom,
  onActualSize,
  onFit,
  onAnnotations,
  tools,
  extras,
  notes = false,
}: {
  /** Controls for the step shown, drawn last, such as the findings toggle. */
  extras?: ReactNode;
  /** Offer the notes toggle: a design has notes; a block definition does not. */
  notes?: boolean;
  /** The editing tools, drawn first in the same palette: the canvas has no toolbar row of its own. */
  tools?: ReactNode;
  zoom: number;
  canFit: boolean;
  annotations: SceneAnnotations;
  onZoom: (factor: number) => void;
  onActualSize: () => void;
  onFit: () => void;
  onAnnotations: (change: Partial<SceneAnnotations>) => void;
}) {
  const percent = Math.round(zoom * 100);
  /**
   * Each side cycles through three states: off, highlighted, then shown on
   * its own with everything else hidden, then off again. The other side's
   * button turns this one's off.
   */
  const washButton = (side: "inside" | "outside", name: string, glyph: ReactNode) => {
    const on = annotations.wash === side;
    const alone = on && Boolean(annotations.isolate);
    const next: Partial<SceneAnnotations> = !on
      ? { wash: side, isolate: false }
      : !alone
        ? { wash: side, isolate: true }
        : { wash: "none", isolate: false };
    return (
      <button
        type="button"
        aria-label={`Highlight ${name}`}
        aria-pressed={on}
        // The pressed state says on; this says how far.
        aria-description={alone ? `Only ${name} shown` : on ? `${name} highlighted` : undefined}
        data-wash-state={alone ? "isolated" : on ? "highlighted" : "off"}
        title={!on ? `Highlight ${name}` : !alone ? `Show only ${name}` : "Show everything"}
        className={alone ? "ws-view-on ws-view-alone" : on ? "ws-view-on" : undefined}
        onClick={() => onAnnotations(next)}
      >
        {glyph}
      </button>
    );
  };
  return (
    <div className="ws-view" role="toolbar" aria-label="Canvas tools">
      {tools && (
        <>
          {tools}
          <span className="ws-view-sep" aria-hidden="true" />
        </>
      )}
      <div className="ws-view-group" role="group" aria-label="View">
        <button type="button" aria-label="Zoom out" title="Zoom out  −" onClick={() => onZoom(1 / ZOOM_STEP)}>
          −
        </button>
        <button
          type="button"
          className="ws-view-zoom"
          aria-label={`Zoom ${percent}%, reset to 100%`}
          title="Reset to 100%"
          onClick={onActualSize}
        >
          {percent}%
        </button>
        <button type="button" aria-label="Zoom in" title="Zoom in  +" onClick={() => onZoom(ZOOM_STEP)}>
          +
        </button>
        <button
          type="button"
          aria-label="Zoom to extents"
          title="Zoom to extents  ⇧1"
          disabled={!canFit}
          onClick={onFit}
        >
          <ZoomExtentsIcon />
        </button>
        <span className="ws-view-sep" aria-hidden="true" />
        {/* Toggles: pressed means shown. View only; the design is untouched. */}
        <button
          type="button"
          aria-label="Labels"
          aria-pressed={annotations.labels}
          title={annotations.labels ? "Hide labels" : "Show labels"}
          className={annotations.labels ? "ws-view-on" : undefined}
          onClick={() => onAnnotations({ labels: !annotations.labels })}
        >
          <LabelsIcon />
        </button>
        <button
          type="button"
          aria-label="Dimensions"
          aria-pressed={annotations.dimensions}
          title={annotations.dimensions ? "Hide dimensions" : "Show dimensions"}
          className={annotations.dimensions ? "ws-view-on" : undefined}
          onClick={() => onAnnotations({ dimensions: !annotations.dimensions })}
        >
          <DimensionsIcon />
        </button>
        <span className="ws-view-sep" aria-hidden="true" />
        <button
          type="button"
          aria-label="Fluid lines"
          aria-pressed={annotations.fluidLines}
          title={annotations.fluidLines ? "Hide fluid lines" : "Show fluid lines"}
          className={annotations.fluidLines ? "ws-view-on" : undefined}
          onClick={() => onAnnotations({ fluidLines: !annotations.fluidLines })}
        >
          <FluidLinesIcon />
        </button>
        <button
          type="button"
          aria-label="Air lines"
          aria-pressed={annotations.airLines}
          title={annotations.airLines ? "Hide air lines" : "Show air lines"}
          className={annotations.airLines ? "ws-view-on" : undefined}
          onClick={() => onAnnotations({ airLines: !annotations.airLines })}
        >
          <AirLinesIcon />
        </button>
        {notes && (
          <button
            type="button"
            aria-label="Notes"
            aria-pressed={annotations.notes}
            title={annotations.notes ? "Hide notes" : "Show notes"}
            className={annotations.notes ? "ws-view-on" : undefined}
            onClick={() => onAnnotations({ notes: !annotations.notes })}
          >
            <NotesIcon />
          </button>
        )}
        <span className="ws-view-sep" aria-hidden="true" />
        {/* One side of the external interfaces washed, or neither: pressing
            the washed side again clears it. */}
        <div className="ws-view-group" role="group" aria-label="Highlight">
          {washButton("inside", "SUS", <InsideIcon />)}
          {washButton("outside", "External", <OutsideIcon />)}
        </div>
        {extras && (
          <>
            <span className="ws-view-sep" aria-hidden="true" />
            {extras}
          </>
        )}
      </div>
    </div>
  );
}

const EXPORTS: readonly { format: StillFormat; label: string }[] = [
  { format: "svg", label: "SVG, vector, transparent background, choose tables…" },
  { format: "pdf", label: "PDF, vector, A3, choose pages…" },
  { format: "png", label: "PNG, high resolution, transparent background" },
];

/**
 * Still images of the whole drawing, as the screen draws it (D35), or a
 * script's steps: one button, and a menu of formats. The menu is the
 * editor's, so it keeps the keyboard behaviour of the WAI-ARIA menu pattern.
 */
export function ExportControls<F extends string = StillFormat>({
  disabled,
  exporting,
  onExport,
  formats = EXPORTS as unknown as readonly { format: F; label: string }[],
  label = "Export image",
}: {
  disabled: boolean;
  exporting: F | null;
  onExport: (format: F) => void;
  formats?: readonly { format: F; label: string }[];
  /** The button's name; the menu is named "<label> as". */
  label?: string;
}) {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [menu, setMenu] = useState<{ x: number; y: number } | null>(null);
  const open = () => {
    const box = buttonRef.current?.getBoundingClientRect();
    setMenu({ x: box?.left ?? 0, y: (box?.bottom ?? 0) + 6 });
  };
  const close = useCallback(() => setMenu(null), []);
  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className="ws-export"
        aria-label={label}
        title={label}
        aria-haspopup="menu"
        aria-expanded={menu !== null}
        aria-busy={exporting !== null || undefined}
        disabled={disabled || exporting !== null}
        // Down, not click: the menu closes on a mousedown outside it, so a
        // second press on the button closes it rather than reopening it.
        onMouseDown={(event) => {
          if (menu) event.preventDefault();
        }}
        onClick={() => (menu ? close() : open())}
        onKeyDown={(event) => {
          if (event.key === "ArrowDown") {
            event.preventDefault();
            open();
          }
        }}
      >
        <ExportIcon />
      </button>
      {menu && (
        <ContextMenu
          x={menu.x}
          y={menu.y}
          label={`${label} as`}
          entries={formats.map((entry) => ({ label: entry.label, onSelect: () => onExport(entry.format) }))}
          onClose={close}
        />
      )}
    </>
  );
}

// --- The workspace --------------------------------------------------------------

export function DesignWorkspace({
  design: initialDesign,
  initialDraft,
  latestVersion,
  onPublished,
  subtitle,
  history,
}: {
  /** A line under the name, e.g. what the design was forked from. */
  subtitle?: ReactNode;
  /** The version history, opened from the header into a drawer. */
  history?: ReactNode;
  design: Design;
  initialDraft: Draft;
  /** The latest published version, for "unpublished changes". */
  latestVersion: { id: string; number: number } | null;
  onPublished: () => void;
}) {
  const designId = initialDesign.id;
  const { user } = useAuth();
  const [design, setDesign] = useState(initialDesign);
  const designRef = useRef(design);
  designRef.current = design;
  // Microfluidic device or tubing set (D34). A setting of the design, which
  // the header can switch.
  const kind: DesignKind = design.kind ?? "microfluidic";
  const [state, dispatch] = useReducer(editorReducer, undefined, () => ({
    ...createEditorState(asDocument(initialDraft.content), {
      designKind: initialDesign.kind ?? "microfluidic",
      revision: initialDraft.revision,
      reserved: initialDraft.reserved_local_ids,
    }),
    // The designer's own view preferences, from the last time they changed them.
    annotations: loadAnnotations(),
    grid: loadGrid(),
    userDefaults: loadUserDefaults(),
  }));
  const stateRef = useRef(state);
  stateRef.current = state;

  // Remember what is shown (labels, dimensions, kinds of line) for the next design and session.
  const annotationsLoaded = useRef(state.annotations);
  useEffect(() => {
    if (state.annotations === annotationsLoaded.current) return;
    saveAnnotations(state.annotations);
  }, [state.annotations]);
  // The grid is set on the settings page; the canvas menu's toggles keep it too.
  const gridLoaded = useRef(state.grid);
  useEffect(() => {
    if (state.grid === gridLoaded.current) return;
    saveGrid(state.grid);
  }, [state.grid]);

  const [library, setLibrary] = useState<WorkspaceLibrary>(EMPTY_WORKSPACE_LIBRARY);
  const libraryRef = useRef(library);
  libraryRef.current = library;
  // Placement defaults are kept by template entity (D36): tell the editor which is which.
  useEffect(() => {
    const entities = Object.fromEntries(Object.values(library.versions).map((entry) => [entry.versionId, entry.entityId]));
    dispatch({ type: "SET_TEMPLATE_ENTITIES", entities });
  }, [library.versions]);
  const [libraryLoading, setLibraryLoading] = useState(true);
  const [profiles, setProfiles] = useState<LibraryEntity[]>([]);
  const [profileLabel, setProfileLabel] = useState<string | null>(null);
  // What volumes and flows read in (D49): the server's resolution, and the
  // project's own setting, which is what a quantity the design leaves unset inherits.
  const liquid = useMemo(() => liquidUnits(design.effective_display_units, kind), [design.effective_display_units, kind]);
  const [projectUnits, setProjectUnits] = useState<DisplayUnitsSetting | null>(null);
  // The parts on the bill of materials line under the pointer, picked out on the drawing.
  const [bomSpotlight, setBomSpotlight] = useState<ReadonlySet<string> | undefined>(undefined);
  const spotlightBom = useCallback(
    (refs: readonly string[] | null) => setBomSpotlight(refs && refs.length > 0 ? new Set(refs) : undefined),
    [],
  );
  useEffect(() => {
    let live = true;
    api
      .getProject(design.project_id)
      .then((project) => live && setProjectUnits(project.display_units ?? null))
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [design.project_id]);

  const [saveStatus, setSaveStatus] = useState<SaveStatus>("idle");
  const [saveFindings, setSaveFindings] = useState<Finding[]>([]);
  const [problem, setProblem] = useState<string | null>(null);
  const refusedRef = useRef<DesignDocument | null>(null);
  const savingRef = useRef(false);
  const [conflict, setConflict] = useState<Conflict | null>(null);
  const conflictRef = useRef(conflict);
  conflictRef.current = conflict;

  const [report, setReport] = useState<CheckReport | null>(null);
  const [checkedDocument, setCheckedDocument] = useState<DesignDocument | null>(null);
  // The design's profile setting when the report was produced.
  const [checkedProfile, setCheckedProfile] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);
  const [checkProblem, setCheckProblem] = useState<string | null>(null);
  const [unpublished, setUnpublished] = useState<boolean | null>(null);
  const [focus, setFocus] = useState<string | null>(null);
  const [message, setMessage] = useState("");
  const [publishing, setPublishing] = useState(false);
  /** Asking whether to revert the draft to the latest version, and doing it. */
  const [revertAsk, setRevertAsk] = useState(false);
  const [reverting, setReverting] = useState(false);
  const [revertError, setRevertError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [step, setStep] = useState<WorkflowStep>("schematic");
  // Each side pane folds away to its own tab, and remembers it: the right one,
  // and each step's left one.
  const [inspectorOpen, setInspectorOpen] = useState(() => loadPaneOpen("inspector"));
  const [checksOpen, setChecksOpen] = useState(() => loadPaneOpen("checks"));
  const [scriptsOpen, setScriptsOpen] = useState(() => loadPaneOpen("scripts"));
  const stepPanelId = useId();

  // Block operations (D20): server writes that return the new draft.
  const [updates, setUpdates] = useState<BlockUpdate[]>([]);
  const [dismissedUpdates, setDismissedUpdates] = useState<string | null>(null);
  const updatesKey = updates.map((update) => `${update.instance_local_id}@${update.latest_version_id}`).join(" ");
  const operatingRef = useRef(false);
  const [operating, setOperating] = useState(false);
  const [saveBlockAsk, setSaveBlockAsk] = useState<string[] | null>(null);
  const [defaultsOpen, setDefaultsOpen] = useState(false);
  const [detachAsk, setDetachAsk] = useState<string | null>(null);
  const [startFromOpen, setStartFromOpen] = useState(false);
  const [dialogError, setDialogError] = useState<unknown>(null);
  const goTo = useNavigate();

  const [canvasBox, size] = useSize<HTMLDivElement>();
  const inspectorRef = useRef<HTMLDivElement>(null);

  // --- Loading ---------------------------------------------------------------

  useEffect(() => {
    let cancelled = false;
    setLibraryLoading(true);
    Promise.all([
      loadWorkspaceLibrary(design.project_id, stateRef.current.document),
    ])
      .then(([loaded]) => {
        if (cancelled) return;
        setLibrary(loaded);
        dispatch({ type: "LIBRARY_LOADED", library: loaded.scene });
        // The editor's own picks, from this kind of design's templates (D34).
        const connector = latestConnector(loaded, kind);
        if (connector) dispatch({ type: "SET_CONNECTOR_TEMPLATE", templateVersionId: connector });
        const junction = latestJunction(loaded, kind);
        if (junction) dispatch({ type: "SET_JUNCTION_TEMPLATE", templateVersionId: junction });
        const airLine = latestAirLine(loaded, kind);
        if (airLine) dispatch({ type: "SET_AIR_LINE_TEMPLATE", templateVersionId: airLine });
      })
      .catch((error: unknown) => {
        if (!cancelled) setProblem(`The library could not be loaded: ${error instanceof Error ? error.message : error}`);
      })
      .finally(() => {
        if (!cancelled) setLibraryLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // Loaded once per design; later pins are resolved by extendLibrary.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [designId]);

  // The profiles this kind of design can be checked against, again whenever it changes kind.
  useEffect(() => {
    let cancelled = false;
    api
      .listLibrary("profiles", design.project_id)
      .then((list) => profilesForKind(list, kind))
      .then((list) => !cancelled && setProfiles(list))
      .catch((error: unknown) => !cancelled && setProblem(`The profiles could not be loaded: ${errorText(error)}`));
    return () => {
      cancelled = true;
    };
  }, [design.project_id, kind]);

  // A pinned profile that is not the latest of its entity is named by fetching it.
  const profileId = design.manufacturing_profile_version_id ?? null;
  useEffect(() => {
    if (!profileId) {
      setProfileLabel(null);
      return;
    }
    const listed = profiles.find((profile) => profile.latest_version_id === profileId);
    if (listed) {
      setProfileLabel(`${listed.name} v${listed.latest_version_number}`);
      return;
    }
    let cancelled = false;
    api
      .getLibraryVersion(profileId)
      .then((version) => {
        const entity = profiles.find((profile) => profile.id === version.entity_id);
        if (!cancelled) setProfileLabel(`${entity?.name ?? "Profile"} v${version.number} (pinned)`);
      })
      .catch(() => !cancelled && setProfileLabel("Unavailable profile"));
    return () => {
      cancelled = true;
    };
  }, [profileId, profiles]);

  // Frame the drawing once the canvas has been measured.
  const framed = useRef(false);
  useEffect(() => {
    if (framed.current || libraryLoading || size.width < 100) return;
    framed.current = true;
    if ((state.document.nodes ?? []).length === 0) return;
    const bounds = buildScene(state.document, state.library, [], DEFAULT_VIEW).bounds;
    dispatch({ type: "SET_VIEWPORT", viewport: fitToBounds(bounds, size.width, size.height) });
  }, [size, libraryLoading, state.document, state.library]);

  // --- Checking and publication state ----------------------------------------

  // By id, not by object: the page builds a fresh `latestVersion` each render.
  const latestId = latestVersion?.id ?? null;
  const refreshUnpublished = useCallback(async () => {
    if (!latestId) {
      setUnpublished(null);
      return;
    }
    try {
      const diff = await api.diffDraft(designId, latestId);
      setUnpublished(diff.entries.length > 0);
    } catch {
      setUnpublished(null);
    }
  }, [designId, latestId]);

  const check = useCallback(
    async (document: DesignDocument) => {
      setChecking(true);
      setCheckProblem(null);
      try {
        const profileAtCheck = designRef.current.manufacturing_profile_version_id ?? null;
        setReport(await api.checkDesign(designId));
        setCheckedDocument(document);
        setCheckedProfile(profileAtCheck);
      } catch (error) {
        if (error instanceof ApiError && error.findings.some((finding) => finding.code === "no_profile")) {
          setCheckProblem("Choose a manufacturing profile to check against.");
        } else {
          setCheckProblem(`Checking failed: ${error instanceof Error ? error.message : String(error)}`);
        }
      } finally {
        setChecking(false);
      }
    },
    [designId],
  );

  // --- Saving ------------------------------------------------------------------

  /** Someone else's save got there first: show theirs beside mine, merge nothing. */
  const presentConflict = useCallback(async () => {
    try {
      const theirs = await api.getDraft(designId);
      setConflict({
        theirs,
        changes: elementChanges(stateRef.current.savedDocument, asDocument(theirs.content)),
        byMe: user !== null && theirs.updated_by === user.user_id,
      });
    } catch (reload) {
      setProblem(`The draft changed elsewhere and could not be reloaded: ${String(reload)}`);
    }
  }, [designId, user]);

  /** Write the working document. Resolves true when the server holds it. */
  const save = useCallback(
    async (overRevision?: number): Promise<boolean> => {
      const current = stateRef.current;
      if (savingRef.current) return false;
      if (!hasUnsavedChanges(current) && overRevision === undefined) return true;
      const document = current.document;
      const revision = overRevision ?? current.revision;
      if (revision === null) return false;
      savingRef.current = true;
      setSaveStatus("saving");
      try {
        const saved = await api.updateDraft(designId, document as Record<string, unknown>, revision);
        dispatch({
          type: "DRAFT_SAVED",
          document,
          revision: saved.revision,
          retired: (saved.content as DesignDocument).retired_local_ids ?? [],
          reserved: saved.reserved_local_ids,
        });
        refusedRef.current = null;
        setSaveFindings([]);
        setConflict(null);
        setSaveStatus("idle");
        setProblem(null);
        void refreshUnpublished();
        // No check here: during capture a half-drawn design fails most rules,
        // and findings nobody asked for are noise. Checking is explicit.
        return true;
      } catch (error) {
        setSaveStatus("failed");
        if (error instanceof ApiError && error.isConflict) {
          await presentConflict();
        } else if (error instanceof ApiError && error.findings.length > 0) {
          refusedRef.current = document;
          setSaveFindings(error.findings.map((finding) => locateSchemaFinding(document, finding)));
        } else {
          setProblem(`Saving failed: ${error instanceof Error ? error.message : String(error)}`);
        }
        return false;
      } finally {
        savingRef.current = false;
      }
    },
    [designId, refreshUnpublished, presentConflict],
  );

  // Debounced autosave. Not while dragging (a move is one edit), not during a
  // conflict (the designer is choosing), and not for a document the server has
  // already refused.
  const unsaved = hasUnsavedChanges(state);
  useEffect(() => {
    if (!unsaved || state.drag || conflict || operating || state.document === refusedRef.current) return;
    const timer = window.setTimeout(() => void save(), SAVE_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [unsaved, state.document, state.drag, conflict, operating, save]);

  // Initial publication state. Not a check: that waits to be asked for.
  useEffect(() => {
    void refreshUnpublished();
  }, [refreshUnpublished]);

  // Pins in a document this session has not loaded (after adopting theirs, or
  // an undo past such a change) are fetched so the scene can draw them.
  useEffect(() => {
    if (libraryLoading) return;
    let cancelled = false;
    extendLibrary(libraryRef.current, state.document).then((extended) => {
      if (cancelled || extended === libraryRef.current) return;
      setLibrary(extended);
      dispatch({ type: "LIBRARY_LOADED", library: extended.scene });
    });
    return () => {
      cancelled = true;
    };
  }, [state.document, libraryLoading]);

  // Leaving with unsaved work asks first.
  useEffect(() => {
    if (!unsaved) return;
    const warn = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [unsaved]);

  // --- Actions -------------------------------------------------------------------

  const keepMine = () => {
    if (conflict) void save(conflict.theirs.revision);
  };

  const adoptTheirs = () => {
    if (!conflict) return;
    dispatch({
      type: "DRAFT_ADOPTED",
      document: asDocument(conflict.theirs.content),
      revision: conflict.theirs.revision,
      reserved: conflict.theirs.reserved_local_ids,
    });
    setConflict(null);
    setSaveStatus("idle");
    setSaveFindings([]);
  };

  const runCheck = async () => {
    // The server checks the saved draft, so what is on screen is saved first.
    // A refused save leaves the old findings, with the refusal beside them.
    if (hasUnsavedChanges(stateRef.current) && !(await save())) return;
    await check(stateRef.current.document);
  };

  /**
   * Prune the draft back to the latest published version. The server does
   * it at the revision this editor last saved, so a colleague's newer save is
   * a conflict, not silently undone. What was discarded goes on the undo
   * stack: Undo brings it back, and the next save keeps it.
   */
  const revert = async () => {
    if (!latestVersion) return;
    setReverting(true);
    setRevertError(null);
    try {
      const draft = await api.revertDraft(designId, stateRef.current.revision ?? 0);
      dispatch({
        type: "DRAFT_ADOPTED",
        document: asDocument(draft.content),
        revision: draft.revision,
        reserved: draft.reserved_local_ids,
      });
      setSaveStatus("idle");
      setSaveFindings([]);
      setRevertAsk(false);
      setNotice(`Reverted to v${latestVersion.number}. Undo brings your changes back.`);
      void refreshUnpublished();
    } catch (error) {
      setRevertError(
        error instanceof ApiError && error.isConflict
          ? "Someone saved this design since you opened it. Reload to see their changes first."
          : `Reverting failed: ${error instanceof Error ? error.message : String(error)}`,
      );
    } finally {
      setReverting(false);
    }
  };

  const publish = async () => {
    setPublishing(true);
    setNotice(null);
    try {
      if (!(await save())) return;
      const version = await api.publish(designId, message.trim());
      // Publishing moves the draft on: its revision advances, and it now
      // retires what the previous version had and this one lacks (D29).
      // Taking both keeps the next save from reading as a conflict and the
      // allocator from reusing a name that has just been retired.
      const rebased = await api.getDraft(designId);
      dispatch({
        type: "DRAFT_SAVED",
        document: stateRef.current.savedDocument,
        revision: rebased.revision,
        retired: (rebased.content as DesignDocument).retired_local_ids ?? [],
        reserved: rebased.reserved_local_ids,
      });
      setMessage("");
      setNotice(`Published version ${version.number}. It is now immutable.`);
      onPublished();
    } catch (error) {
      setProblem(`Publishing failed: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      setPublishing(false);
    }
  };

  /**
   * Switch between microfluidic device and tubing set (D34). The content is
   * untouched; the profile is cleared by the server, the palette and the
   * editor's own picks follow, and a check will name anything of the old kind.
   */
  const changeKind = async (next: DesignKind) => {
    if (next === kind) return;
    try {
      const updated = await api.setKind(designId, next);
      setDesign(updated);
      dispatch({ type: "SET_DESIGN_KIND", kind: next });
      const current = libraryRef.current;
      const connector = latestConnector(current, next);
      if (connector) dispatch({ type: "SET_CONNECTOR_TEMPLATE", templateVersionId: connector });
      const junction = latestJunction(current, next);
      if (junction) dispatch({ type: "SET_JUNCTION_TEMPLATE", templateVersionId: junction });
      const airLine = latestAirLine(current, next);
      if (airLine) dispatch({ type: "SET_AIR_LINE_TEMPLATE", templateVersionId: airLine });
      setNotice(
        `This is now a ${next === "tubing_set" ? "tubing set" : "microfluidic device"}. Its profile was cleared; choose one of the new kind. Elements already drawn are kept, and a check names any made for the other kind.`,
      );
    } catch (error) {
      setProblem(`The kind could not be changed: ${errorText(error)}`);
    }
  };

  const changeUnits = async (next: DisplayUnitsSetting | null) => {
    try {
      setDesign(await api.setDesignDisplayUnits(designId, next as DisplayUnitsBody));
    } catch (error) {
      setProblem(`The units could not be changed: ${errorText(error)}`);
    }
  };

  const changeProfile = async (versionId: string | null) => {
    try {
      const updated = await api.setProfile(designId, versionId);
      // Earlier findings were against the old profile; they stay, marked as
      // out of date, until the next check.
      setDesign(updated);
    } catch (error) {
      setProblem(`The profile could not be changed: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  const sceneFor = useCallback(
    () => buildScene(stateRef.current.document, stateRef.current.library, [], {
      ...DEFAULT_VIEW,
      collapsed: stateRef.current.collapsed,
    }),
    [],
  );

  // --- Still export (D35) --------------------------------------------------------

  const [exporting, setExporting] = useState<StillFormat | null>(null);
  // What a PDF or SVG carries, being chosen; null when no such export is being set up.
  const [exportOptions, setExportOptions] = useState<{ format: "pdf" | "svg"; groups: ExportChoiceGroup[] } | null>(null);
  /**
   * The canvas's own scene view, less the grid: a still has no background.
   * The PDF redraws it per page with its own wash and notes (PDF_VIEWS).
   */
  const drawCurrent = (): DrawScene => {
    const current = stateRef.current;
    const view = sceneViewFor(current, null, liquid);
    return (annotations) =>
      buildScene(current.document, current.library, [], {
        ...view,
        grid: { ...view.grid, visible: false },
        annotations: { ...view.annotations!, ...annotations },
      });
  };
  const exportAs = async (format: StillFormat, chosen?: ReadonlySet<string>) => {
    if (format !== "png" && !chosen) {
      // Which pages and tables, asked each time.
      const bomUnavailable = hasBillOfMaterials(stateRef.current.document, libraryRef.current)
        ? null
        : "No line crosses an external interface";
      const groups = format === "pdf" ? pdfChoices(pdfAvailability(drawCurrent()), bomUnavailable) : svgChoices(bomUnavailable);
      setExportOptions({ format, groups });
      return;
    }
    setExportOptions(null);
    const current = stateRef.current;
    const tables = chosen ? bomTables(current.document, libraryRef.current, liquid, chosen) : [];
    const draw = drawCurrent();
    const pending = hasUnsavedChanges(current);
    // Only a drawing known to match the latest version is labelled as it.
    const published = latestVersion !== null && !pending && unpublished === false;
    setExporting(format);
    setProblem(null);
    try {
      await exportStill(draw, {
        name: designRef.current.name,
        version: published ? latestVersion.number : null,
        revision: current.revision,
        unsaved: pending,
      }, format, chosen && pdfPagesChosen(chosen).pages, tables);
    } catch (error) {
      setProblem(`The ${format.toUpperCase()} could not be exported: ${errorText(error)}`);
    } finally {
      setExporting(null);
    }
  };

  // --- A script's steps, a page each ----------------------------------------------

  const [exportingSteps, setExportingSteps] = useState<StepsFormat | null>(null);
  const exportScriptSteps = async (format: StepsFormat) => {
    const script = scripts.script;
    if (!script) return;
    const current = stateRef.current;
    const pending = hasUnsavedChanges(current);
    const published = latestVersion !== null && !pending && unpublished === false;
    const source = {
      name: designRef.current.name,
      version: published ? latestVersion.number : null,
      revision: current.revision,
      unsaved: pending,
    };
    const scriptName = scripts.scripts.find((candidate) => candidate.id === script.id)?.name ?? "Script";
    const view = sceneViewFor(current, null, liquid);
    // The Scripts view's drawing, step by step, less the grid.
    const draw = (preview: StepPreview) =>
      buildScene(current.document, current.library, [], { ...view, preview, grid: { ...view.grid, visible: false } });
    const pages = stepPages(script.content, scripts.previewAt, current.document, scripts.parent?.name ?? null, liquid);
    setExportingSteps(format);
    setProblem(null);
    try {
      await exportSteps(stepSheets(pages, draw, source, scriptName), stepsFilename(source, scriptName, format), format);
    } catch (error) {
      setProblem(`The steps could not be exported: ${errorText(error)}`);
    } finally {
      setExportingSteps(null);
    }
  };

  /** Select an element and bring it to the centre of the canvas. */
  const navigate = useCallback(
    (ref: string) => {
      const current = stateRef.current;
      // A member of a collapsed block is not drawn; the block stands in for it.
      const instance = lockingInstance(current.document, ref);
      const target = instance && current.collapsed.has(instance.local_id) ? instance.local_id : ref;
      dispatch({ type: "SELECT", refs: [target] });
      const scene = sceneFor();
      const centre =
        scene.nodes.find((node) => node.ref === target)?.centre ??
        scene.edges.find((edge) => edge.ref === target)?.labelPosition ??
        scene.blocks.find((block) => block.ref === target)?.centre;
      if (centre) {
        const { zoom } = current.viewport;
        dispatch({
          type: "SET_VIEWPORT",
          viewport: { zoom, x: centre.x - size.width / (2 * zoom), y: centre.y - size.height / (2 * zoom) },
        });
      }
    },
    [sceneFor, size],
  );

  // --- Block operations (D20) --------------------------------------------------

  /** Which instances have a newer definition version: the prompt (requirement 8.8). */
  const refreshUpdates = useCallback(async () => {
    try {
      setUpdates(await api.blockUpdates(designId));
    } catch {
      // Advisory: without it the prompt is late, never wrong.
    }
  }, [designId]);

  // On opening, and on coming back to the tab: a definition is usually
  // published from its own view, in another tab or by a colleague.
  useEffect(() => {
    void refreshUpdates();
    const onFocus = () => void refreshUpdates();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [refreshUpdates]);

  /**
   * Run a server operation on the draft. The server works on what it holds,
   * so the screen is saved first; nothing autosaves while it runs. Resolves
   * null when it could not start (a refused save or a conflict, both already
   * shown), and throws what the server refused, for the caller to present.
   */
  const operate = useCallback(
    async <T,>(op: (revision: number) => Promise<T>): Promise<T | null> => {
      if (conflictRef.current || operatingRef.current) return null;
      operatingRef.current = true;
      setOperating(true);
      try {
        while (savingRef.current) await new Promise((resolve) => window.setTimeout(resolve, 20));
        if (hasUnsavedChanges(stateRef.current) && !(await save())) return null;
        const revision = stateRef.current.revision;
        if (revision === null) return null;
        try {
          return await op(revision);
        } catch (error) {
          if (error instanceof ApiError && error.isConflict) {
            await presentConflict();
            return null;
          }
          throw error;
        }
      } finally {
        operatingRef.current = false;
        setOperating(false);
      }
    },
    [save, presentConflict],
  );

  /** Take the draft an operation returned. What was on screen stays one undo away. */
  const adopt = useCallback(
    (draft: Draft) => {
      dispatch({
        type: "DRAFT_ADOPTED",
        document: asDocument(draft.content),
        revision: draft.revision,
        reserved: draft.reserved_local_ids,
      });
      setSaveFindings([]);
      setSaveStatus("idle");
      void refreshUpdates();
      void refreshUnpublished();
    },
    [refreshUpdates, refreshUnpublished],
  );

  /** The palette again: a block was saved, so it is placeable now. */
  const refreshBlocks = useCallback(async () => {
    const reloaded = await reloadBlocks(libraryRef.current, design.project_id);
    libraryRef.current = reloaded;
    setLibrary(reloaded);
    dispatch({ type: "LIBRARY_LOADED", library: reloaded.scene });
  }, [design.project_id]);

  const saveBlock = async (form: { name: string; libraryScoped: boolean; summary: string }) => {
    const refs = saveBlockAsk ?? [];
    setDialogError(null);
    try {
      const saved = await operate((revision) =>
        api.saveBlock(designId, {
          selection: refs,
          name: form.name,
          library_scoped: form.libraryScoped,
          summary: form.summary,
          expected_revision: revision,
        }),
      );
      if (!saved) return;
      // The library first, so the new instance is drawn with its name.
      await refreshBlocks().catch(() => undefined);
      if (saved.draft) adopt(saved.draft);
      if (saved.instance_local_id) dispatch({ type: "SELECT", refs: [saved.instance_local_id] });
      setSaveBlockAsk(null);
      const where = form.libraryScoped ? "the shared library" : "this project";
      const notes = (saved.findings ?? []).map((finding) => finding.message).join(" ");
      setNotice(`Saved ${form.name} v1 to ${where}; the selection is now ${saved.instance_local_id}.${notes ? ` ${notes}` : ""}`);
    } catch (error) {
      setDialogError(error);
    }
  };

  const placeBlock = async (versionId: string, at: Point) => {
    setProblem(null);
    const current = stateRef.current;
    const definition = libraryRef.current.scene.definitions[versionId];
    // The definition is laid out from its own origin; `at` is where its middle goes.
    const middle = definition ? definitionCentre(definition.content) : { x: 0, y: 0 };
    const spacing = current.grid.snap ? current.grid.spacing : 1;
    const snap = (value: number) => Math.round(value / spacing) * spacing;
    const origin = { x: snap(at.x - middle.x), y: snap(at.y - middle.y) };
    try {
      const placed = await operate((revision) => api.placeBlock(designId, versionId, revision, origin));
      if (!placed) return;
      adopt(placed.draft);
      // Placed as a block: collapsed to its symbol, PCB-style. Expanding is a
      // view change, a double-click away.
      dispatch({ type: "SET_COLLAPSED", instance: placed.instance_local_id, collapsed: true });
      dispatch({ type: "SELECT", refs: [placed.instance_local_id] });
    } catch (error) {
      setProblem(`The block could not be placed: ${errorText(error)}`);
    }
  };

  const detachBlock = async () => {
    const instance = detachAsk;
    if (!instance) return;
    setDialogError(null);
    try {
      const detached = await operate((revision) => api.detachBlock(designId, instance, revision));
      if (!detached) return;
      adopt(detached.draft);
      setDetachAsk(null);
      const entry = libraryRef.current.versions[detached.definition_version_id];
      setNotice(
        `Detached ${instance}: its ${detached.former_members.length} members are ordinary components now, recorded as from ${
          entry ? `${entry.name} v${entry.number}` : "its definition"
        }.`,
      );
    } catch (error) {
      setDialogError(error);
    }
  };

  const updateBlock = async (instance: string) => {
    setProblem(null);
    const pending = updates.find((update) => update.instance_local_id === instance);
    try {
      const updated: UpdatedBlock | null = await operate((revision) => api.updateBlock(designId, instance, revision));
      if (!updated) return;
      adopt(updated.draft);
      setNotice(describeUpdate(updated, pending));
    } catch (error) {
      setProblem(`${instance} could not be updated: ${errorText(error)}`);
    }
  };

  /** A definition opens in its own view, apart from any design (requirement 8.7). */
  const openDefinition = async (instance: string) => {
    const pinned = stateRef.current.document.group_instances?.find((candidate) => candidate.local_id === instance);
    const entry = pinned ? libraryRef.current.versions[pinned.group_definition_version_id] : undefined;
    if (!entry) {
      setProblem(`The definition ${instance} was placed from is not available.`);
      return;
    }
    // Leaving the page would drop a pending autosave.
    if (hasUnsavedChanges(stateRef.current) && !(await save())) return;
    goTo(`/library/blocks/${entry.entityId}`, {
      state: { from: `/designs/${designId}`, fromName: designRef.current.name, version: entry.number },
    });
  };

  // Stable across renders; each operation reads the latest through refs or state setters.
  const operations = useRef({ placeBlock, updateBlock, openDefinition });
  operations.current = { placeBlock, updateBlock, openDefinition };
  const pendingUpdates = useMemo(() => new Map(updates.map((update) => [update.instance_local_id, update])), [updates]);

  const host: EditorHost = useMemo(
    () => ({
      inspect: (ref) => {
        dispatch({ type: "SELECT", refs: [ref] });
        // Asked for by name: a folded inspector opens to show it.
        setInspectorOpen(true);
        // After the inspector renders the selection.
        window.setTimeout(() => {
          inspectorRef.current?.querySelector<HTMLElement>("input:not([disabled]), select:not([disabled])")?.focus();
        }, 0);
      },
      revealFindings: (ref) => {
        setStep("checks");
        setFocus(ref);
      },
      saveAsBlock: (refs) => {
        // Components and channels; a block cannot contain a block (D16).
        const design = stateRef.current.document;
        const elements = refs.filter(
          (ref) =>
            design.nodes?.some((node) => node.local_id === ref) || design.edges?.some((edge) => edge.local_id === ref),
        );
        setDialogError(null);
        setSaveBlockAsk(elements);
      },
      detach: (instance) => {
        setDialogError(null);
        setDetachAsk(instance);
      },
      openDefinition: (instance) => void operations.current.openDefinition(instance),
      updateBlock: (instance) => void operations.current.updateBlock(instance),
      hasNewerVersion: (instance) => pendingUpdates.has(instance),
      placeBlock: (versionId, at) => void operations.current.placeBlock(versionId, at),
      swapCandidates: (ref) => {
        const node = stateRef.current.document.nodes?.find((candidate) => candidate.local_id === ref);
        if (!node) return [];
        return swapCandidates(libraryRef.current, node.role, node.template_version_id, stateRef.current.designKind);
      },
    }),
    [pendingUpdates],
  );

  const blockActions: BlockActions = useMemo(
    () => ({
      detach: (instance) => host.detach?.(instance),
      openDefinition: (instance) => host.openDefinition?.(instance),
      update: (instance) => host.updateBlock?.(instance),
      newerVersion: (instance) => pendingUpdates.get(instance)?.latest_number ?? null,
    }),
    [host, pendingUpdates],
  );

  const place = (versionId: string) =>
    dispatch(placeFromPalette(state, sceneFor(), versionId, size.width, size.height));

  const placeBlockFromPalette = (versionId: string) =>
    void placeBlock(versionId, viewportCentre(state.viewport, size.width, size.height));

  // The editor refused an edit inside a locked block: offer the way out (8.9).
  const lockedOffer = (() => {
    const refusal = state.notice;
    if (refusal?.kind !== "refused" || refusal.code !== "locked_block_member") return null;
    const instances = new Set((state.document.group_instances ?? []).map((instance) => instance.local_id));
    const named = refusal.refs.find((ref) => instances.has(ref));
    const instance = named ?? refusal.refs.map((ref) => lockingInstance(state.document, ref)?.local_id).find(Boolean);
    return instance ?? null;
  })();

  // --- Findings --------------------------------------------------------------------

  const findings: Finding[] = useMemo(
    () => [...saveFindings, ...((report?.findings ?? []) as Finding[])],
    [saveFindings, report],
  );
  const present = useMemo(
    () =>
      new Set([
        ...(state.document.nodes ?? []).map((node) => node.local_id),
        ...(state.document.edges ?? []).map((edge) => edge.local_id),
        ...(state.document.group_instances ?? []).map((instance) => instance.local_id),
      ]),
    [state.document],
  );

  const findingsStale =
    report !== null &&
    (checkedDocument !== state.document || checkedProfile !== (design.manufacturing_profile_version_id ?? null));
  const centre = { x: size.width / 2, y: size.height / 2 };
  const busy = publishing || saveStatus === "saving";
  const defaultsSet = Object.keys(state.document.placement_defaults ?? {}).length;

  const rename = async (name: string): Promise<boolean> => {
    try {
      setDesign(await api.renameDesign(designId, name));
      return true;
    } catch (error) {
      setProblem(`Renaming failed: ${errorText(error)}`);
      return false;
    }
  };

  // Side pane widths, as this person left them: the checks and scripts lists
  // on the left, and the right pane as inspector or scripts editor.
  const [paneWidths, setPaneWidths] = useState(loadPaneWidths);
  const paneWidthsRef = useRef(paneWidths);
  paneWidthsRef.current = paneWidths;
  const resizer = (pane: Pane, label: string, edge: "left" | "right") => (
    <PaneResizer
      label={label}
      width={paneWidths[pane]}
      limits={PANES[pane]}
      edge={edge}
      onResize={(width) => {
        // Kept in the ref at once, so saving straight after (a key press) sees it.
        paneWidthsRef.current = { ...paneWidthsRef.current, [pane]: width };
        setPaneWidths(paneWidthsRef.current);
      }}
      onDone={() => savePaneWidths(paneWidthsRef.current)}
    />
  );

  /** Folding a pane away, or back: a view preference, kept like its width. */
  const foldPane = (pane: FoldablePane, open: boolean, show: (open: boolean) => void) => {
    const next = !open;
    show(next);
    savePaneOpen(pane, next);
  };
  const toggleInspector = () => foldPane("inspector", inspectorOpen, setInspectorOpen);
  const toggleChecks = () => foldPane("checks", checksOpen, setChecksOpen);
  const toggleScriptsPane = () => foldPane("scripts", scriptsOpen, setScriptsOpen);

  // The Scripts step opens with every label shown and the whole design in
  // view; leaving it puts labels back as they were.
  const labelsBeforeScripts = useRef<boolean | null>(null);
  const changeStep = (next: WorkflowStep) => {
    if (next === "scripts" && step !== "scripts") {
      labelsBeforeScripts.current = state.annotations.labels;
      if (!state.annotations.labels) dispatch({ type: "SET_ANNOTATIONS", labels: true });
      if (state.tool !== "select") dispatch({ type: "SET_TOOL", tool: "select" });
      dispatch({ type: "SET_VIEWPORT", viewport: fitToBounds(sceneFor().bounds, size.width, size.height) });
    } else if (step === "scripts" && next !== "scripts" && labelsBeforeScripts.current !== null) {
      if (labelsBeforeScripts.current !== state.annotations.labels) {
        dispatch({ type: "SET_ANNOTATIONS", labels: labelsBeforeScripts.current });
      }
      labelsBeforeScripts.current = null;
    }
    // Checks work on what is drawn: a connection half made during capture is not carried over.
    if (next === "checks" && state.tool !== "select") dispatch({ type: "SET_TOOL", tool: "select" });
    setStep(next);
  };
  // Scripts and condition sets (D42, D43): previewed on the drawing as it stands.
  const scripts = useScripts(design.id, state.document, library.scene.templates, step === "scripts", liquid);
  const scriptCommands = useMemo(
    () => scriptMenu(scripts, state.document, () => !inspectorOpen && toggleInspector()),
    [scripts, state.document, inspectorOpen, toggleInspector],
  );
  const editing = step === "schematic" || step === "checks" || step === "scripts";
  const rightPane: Pane = step === "scripts" ? "scripts-editor" : "inspector";
  const placeholder = WORKFLOW_STEPS.find((candidate) => candidate.id === step && "about" in candidate);
  const checkErrors =
    saveFindings.length + ((report?.findings ?? []) as Finding[]).filter((finding) => finding.severity === "error").length;

  return (
    <DisplayUnitsContext.Provider value={liquid}>
    <div className="ws">
      <header className="ws-header">
        <div className="ws-title">
          <span className="ws-name">
            <DesignName name={design.name} disabled={busy} onRename={rename} />
            <UnpublishedMark
              unpublished={latestVersion === null || unsaved || unpublished === true}
              latestVersion={latestVersion?.number ?? null}
            />
          </span>
          <select
            className="ws-kind"
            value={kind}
            aria-label="Kind of design"
            title="What this design is: which parts the palette offers and which profiles can check it"
            disabled={busy || conflict !== null}
            onChange={(event) => void changeKind(event.target.value as DesignKind)}
          >
            <option value="microfluidic">Microfluidic device</option>
            <option value="tubing_set">Tubing set</option>
          </select>
          {/* Beside the kind: what new parts start with depends on what is being made. */}
          <button
            type="button"
            className="ws-defaults-button"
            title="Placement defaults: what new components and lines start with"
            disabled={libraryLoading}
            onClick={() => setDefaultsOpen(true)}
          >
            <DefaultsIcon />
            <span>Defaults</span>
            {defaultsSet > 0 && (
              <span className="ws-defaults-count">
                <span aria-hidden="true">{defaultsSet}</span>
                <span className="visually-hidden">{`, set for ${defaultsSet} template${defaultsSet === 1 ? "" : "s"}`}</span>
              </span>
            )}
          </button>
          <UnitsPicker
            value={design.display_units ?? null}
            inherited={inheritedUnits(projectUnits, kind)}
            effective={design.effective_display_units}
            inheritedFrom={projectUnits ? "Project" : kind === "tubing_set" ? "Tubing set" : "Microfluidic"}
            disabled={busy || conflict !== null}
            onChange={(next) => void changeUnits(next)}
          />
          {subtitle && <span className="ws-muted ws-subtitle">{subtitle}</span>}
        </div>
        <div className="ws-header-actions">
          <SaveState unsaved={unsaved} status={saveStatus} conflict={conflict !== null} />
          <input
            className="ws-message"
            value={message}
            onChange={(event) => setMessage(event.target.value)}
            placeholder="Version message"
            aria-label="Version message"
          />
          {history && (
            <button type="button" aria-expanded={historyOpen} onClick={() => setHistoryOpen((open) => !open)}>
              History
            </button>
          )}
          {/* A new design starting from this one: the usual starting point for a variant (D9). */}
          <button
            type="button"
            title="Start a new design from this one: copy the draft, or fork a published version"
            disabled={busy || operating || conflict !== null}
            onClick={() =>
              // Saved first, so a copy holds what is on screen.
              void operate(async () => true).then((ready) => ready && setStartFromOpen(true))
            }
          >
            Copy or fork
          </button>
          {latestVersion && unpublished && (
            <button
              type="button"
              title={`Discard every change since v${latestVersion.number}`}
              onClick={() => {
                setRevertError(null);
                setRevertAsk(true);
              }}
              disabled={busy || reverting || conflict !== null}
            >
              Revert to v{latestVersion.number}
            </button>
          )}
          <button
            type="button"
            className="primary"
            onClick={() => void publish()}
            disabled={busy || conflict !== null || saveFindings.length > 0}
          >
            Publish v{(latestVersion?.number ?? 0) + 1}
          </button>
        </div>
      </header>
      <WorkflowSteps step={step} onStep={changeStep} errors={checkErrors} panelId={stepPanelId} />

      {conflict && (
        <ConflictBanner conflict={conflict} busy={busy} onKeepMine={keepMine} onAdoptTheirs={adoptTheirs} />
      )}
      {problem && (
        <Banner className="error" role="alert" onDismiss={() => setProblem(null)}>
          {problem}
        </Banner>
      )}
      {notice && (
        <Banner className="ws-notice" autoDismissMs={NOTICE_DISMISS_MS} resetKey={notice} onDismiss={() => setNotice(null)}>
          {notice}
        </Banner>
      )}
      {updates.length > 0 && updatesKey !== dismissedUpdates && (
        // Dismissed until the set of available updates changes: a newly
        // published version prompts again (requirement 8.8).
        <Banner className="ws-notice" label="Block updates" onDismiss={() => setDismissedUpdates(updatesKey)}>
          <div className="ws-updates">
            {updates.map((update) => {
              const name = library.versions[update.current_version_id]?.name ?? "Block";
              return (
                <span key={update.instance_local_id} className="ws-update">
                  <button type="button" className="ws-link" onClick={() => navigate(update.instance_local_id)}>
                    {update.instance_local_id}
                  </button>{" "}
                  · {name} v{update.current_number}, v{update.latest_number} available{" "}
                  <button
                    type="button"
                    onClick={() => void updateBlock(update.instance_local_id)}
                    disabled={operating || conflict !== null}
                  >
                    Update {update.instance_local_id}
                  </button>
                </span>
              );
            })}
          </div>
        </Banner>
      )}
      {lockedOffer && (
        <Banner className="ws-notice" onDismiss={() => dispatch({ type: "CLEAR_NOTICE" })}>
          <div className="ws-offer">
            Members of {lockedOffer} are locked to its definition.{" "}
            <button type="button" onClick={() => host.detach?.(lockedOffer)}>
              Detach {lockedOffer}…
            </button>{" "}
            <button type="button" onClick={() => host.openDefinition?.(lockedOffer)}>
              Open the definition
            </button>
          </div>
        </Banner>
      )}

      {step === "schematic" && saveFindings.length > 0 && (
        // Not dismissible: until these are fixed, nothing drawn reaches the server.
        <div className="ws-conflict" role="alert">
          The last save was refused ({saveFindings.length} problem{saveFindings.length === 1 ? "" : "s"}); nothing since is
          saved.{" "}
          <button type="button" className="ws-link" onClick={() => changeStep("checks")}>
            Review in Design checks
          </button>
        </div>
      )}

      {placeholder && "about" in placeholder && (
        <section
          className="ws-placeholder"
          role="tabpanel"
          id={stepPanelId}
          aria-labelledby={`${stepPanelId}-tab-${placeholder.id}`}
        >
          <h2>{placeholder.label}</h2>
          <p className="ws-muted">{placeholder.about}</p>
          <p className="ws-muted ws-small">
            {placeholder.phase === 2 ? "Planned for Phase 2." : "Not built yet."}
          </p>
        </section>
      )}

      {/* Kept mounted on the later steps, only hidden: the canvas keeps its
          view and its measured size, and the working document is untouched. */}
      <div
        className={`ws-body${inspectorOpen ? "" : " ws-body-inspector-closed"}`}
        style={inspectorOpen ? ({ "--ws-right": `${paneWidths[rightPane]}px` } as CSSProperties) : undefined}
        hidden={!editing}
        role={editing ? "tabpanel" : undefined}
        id={editing ? stepPanelId : undefined}
        aria-labelledby={editing ? `${stepPanelId}-tab-${step}` : undefined}
      >
        {step === "scripts" ? (
          <div
            className={`ws-left-slot${scriptsOpen ? "" : " ws-left-slot-closed"}`}
            style={scriptsOpen ? { width: paneWidths.scripts } : undefined}
          >
            <PanelTab side="left" open={scriptsOpen} name="scripts" onToggle={toggleScriptsPane} />
            {scriptsOpen && (
              <>
                <ScriptsPanel scripts={scripts} design={state.document} />
                {resizer("scripts", "Resize scripts", "right")}
              </>
            )}
          </div>
        ) : step === "checks" ? (
          <div
            className={`ws-left-slot${checksOpen ? "" : " ws-left-slot-closed"}`}
            style={checksOpen ? { width: paneWidths.checks } : undefined}
          >
            <PanelTab side="left" open={checksOpen} name="checks" onToggle={toggleChecks} />
            {checksOpen && (
              <>
                <FindingsPanel
                  report={report}
                  checkProblem={checkProblem}
                  checking={checking}
                  stale={findingsStale}
                  profileName={profileLabel ?? (profileId ? null : "the project default")}
                  profile={
                    <ProfilePicker
                      profiles={profiles}
                      current={profileId}
                      currentLabel={profileLabel}
                      disabled={libraryLoading}
                      onChange={(versionId) => void changeProfile(versionId)}
                    />
                  }
                  saveFindings={saveFindings}
                  present={present}
                  focus={focus}
                  onNavigate={navigate}
                  onCheck={() => void runCheck()}
                  canCheck={!conflict}
                />
                {resizer("checks", "Resize checks", "right")}
              </>
            )}
          </div>
        ) : (
          <Palette
            library={library}
            kind={kind}
            loading={libraryLoading}
            onPlace={place}
            onPlaceBlock={placeBlockFromPalette}
          />
        )}

        <main className="ws-main">

          <div ref={canvasBox} className={`ws-canvas${findingsStale ? " ws-canvas-stale" : ""}`}>
            <EditorCanvas
              state={state}
              dispatch={dispatch}
              width={size.width}
              height={size.height}
              findings={findings}
              host={host}
              title={`${design.name} schematic`}
              notes
              preview={step === "scripts" ? scripts.preview : null}
              readOnly={step === "scripts"}
              onHistory={step === "scripts" ? (redo) => (redo ? scripts.redo() : scripts.undo()) : undefined}
              menuCommands={step === "scripts" ? scriptCommands : undefined}
              onToggleState={step === "scripts" ? (ref) => scripts.toggleState(ref) : undefined}
              previewEdit={step === "scripts" ? scripts.canvasEdit : null}
              onPreviewEdit={step === "scripts" ? scripts.setCanvasEdit : undefined}
              spotlight={bomSpotlight}
              renderPreviewEdit={(edit, at, close) => (
                <ScriptsCanvasEditor scripts={scripts} design={state.document} edit={edit} at={at} close={close} />
              )}
            />
            {step === "scripts" && scripts.script && <StepNavigator scripts={scripts} design={state.document} />}
            {step === "scripts" && scripts.findingsOpen && (
              <ScriptFindings
                scripts={scripts}
                design={state.document}
                onShow={(refs) => {
                  navigate(refs[0]!);
                  if (refs.length > 1) dispatch({ type: "SELECT", refs });
                }}
              />
            )}
            <ViewControls
              notes
              extras={step === "scripts" ? <FindingsToggle scripts={scripts} /> : undefined}
              tools={
                step === "schematic" ? (
                  <EditorTools state={state} dispatch={dispatch} notes>
                    <ExportControls
                      disabled={
                        (state.document.nodes ?? []).length === 0 && (state.document.group_instances ?? []).length === 0
                      }
                      exporting={exporting}
                      onExport={(format) => void exportAs(format)}
                    />
                  </EditorTools>
                ) : step === "scripts" ? (
                  // The schematic's palette, less what only drawing does: undo steps through the script.
                  <EditorTools state={state} dispatch={dispatch} drawing={false} history={scripts}>
                    <ExportControls
                      label="Export steps"
                      formats={STEPS_EXPORTS}
                      disabled={!scripts.script}
                      exporting={exportingSteps}
                      onExport={(format) => void exportScriptSteps(format)}
                    />
                  </EditorTools>
                ) : undefined
              }
              zoom={state.viewport.zoom}
              canFit={(state.document.nodes ?? []).length > 0}
              annotations={state.annotations}
              onAnnotations={(change) => dispatch({ type: "SET_ANNOTATIONS", ...change })}
              onZoom={(factor) => dispatch({ type: "SET_VIEWPORT", viewport: zoomAbout(state.viewport, factor, centre) })}
              onActualSize={() =>
                dispatch({ type: "SET_VIEWPORT", viewport: zoomAbout(state.viewport, 1 / state.viewport.zoom, centre) })
              }
              onFit={() =>
                dispatch({ type: "SET_VIEWPORT", viewport: fitToBounds(sceneFor().bounds, size.width, size.height) })
              }
            />
          </div>
        </main>

        {historyOpen && history && (
          <aside className="ws-drawer" aria-label="Version history">
            <div className="ws-drawer-head">
              <strong>Version history</strong>
              <button type="button" onClick={() => setHistoryOpen(false)}>
                Close
              </button>
            </div>
            {history}
          </aside>
        )}
        <div ref={inspectorRef} className="ws-inspector-slot">
          <PanelTab side="right" open={inspectorOpen} name="inspector" onToggle={toggleInspector} />
          {inspectorOpen && resizer(rightPane, step === "scripts" ? "Resize scripts editor" : "Resize inspector", "left")}
          {inspectorOpen &&
            (step === "scripts" ? (
              <ScriptsEditor
                scripts={scripts}
                design={state.document}
                templates={library.scene.templates}
                onShow={(refs) => {
                  navigate(refs[0]!);
                  if (refs.length > 1) dispatch({ type: "SELECT", refs });
                }}
              />
            ) : (
              <Inspector
                state={state}
                library={library}
                findings={findings}
                dispatch={dispatch}
                blocks={blockActions}
                onSpotlight={spotlightBom}
              />
            ))}
        </div>
      </div>

      {defaultsOpen && (
        <DefaultsDialog
          library={library}
          kind={kind}
          designDefaults={state.document.placement_defaults ?? {}}
          userDefaults={state.userDefaults}
          onSaveDesign={(defaults) => {
            // An edit of the draft: saved with it, undone with it.
            dispatch({ type: "SET_PLACEMENT_DEFAULTS", defaults });
            setDefaultsOpen(false);
            setNotice("Saved the placement defaults for this design. New components and lines start with them.");
          }}
          onSaveUser={(defaults) => {
            saveUserDefaults(defaults);
            dispatch({ type: "SET_USER_DEFAULTS", defaults });
            setDefaultsOpen(false);
            setNotice(
              "Saved as your defaults, in this browser. They apply wherever a design does not set its own.",
            );
          }}
          onClose={() => setDefaultsOpen(false)}
        />
      )}
      {exportOptions && (
        <ExportOptionsDialog
          title={exportOptions.format === "pdf" ? "Export PDF" : "Export SVG"}
          groups={exportOptions.groups}
          submit={(chosen) => {
            if (exportOptions.format === "svg") return { label: "Export SVG", disabled: false };
            const { count } = pdfPagesChosen(chosen);
            return { label: count === 1 ? "Export 1 page" : `Export ${count} pages`, disabled: count === 0 };
          }}
          onExport={(chosen) => void exportAs(exportOptions.format, chosen)}
          onClose={() => setExportOptions(null)}
        />
      )}
      {saveBlockAsk && (
        <SaveBlockDialog
          count={saveBlockAsk.filter((ref) => state.document.nodes?.some((node) => node.local_id === ref)).length}
          busy={operating}
          error={dialogError}
          onSave={(form) => void saveBlock(form)}
          onCancel={() => setSaveBlockAsk(null)}
        />
      )}
      {revertAsk && latestVersion && (
        <ConfirmDialog
          title={`Revert to v${latestVersion.number}?`}
          confirmLabel={`Revert to v${latestVersion.number}`}
          busyLabel="Reverting…"
          danger
          busy={reverting}
          error={revertError}
          onConfirm={() => void revert()}
          onCancel={() => setRevertAsk(false)}
        >
          <p style={{ margin: 0 }}>
            The draft goes back to the latest published version, v{latestVersion.number}. Every change made since
            is discarded.
          </p>
          <p className="muted" style={{ margin: 0 }}>
            Published versions are not touched. Until you leave this design, Undo brings the changes back.
          </p>
        </ConfirmDialog>
      )}
      {startFromOpen && (
        <StartFromDesignDialog
          design={design}
          onCreated={(created) => {
            setStartFromOpen(false);
            goTo(`/designs/${created.id}`);
          }}
          onCancel={() => setStartFromOpen(false)}
        />
      )}
      {detachAsk && (
        <ConfirmDialog
          title={`Detach ${detachAsk}?`}
          confirmLabel="Detach"
          busyLabel="Detaching…"
          danger={false}
          busy={operating}
          error={dialogError}
          onConfirm={() => void detachBlock()}
          onCancel={() => setDetachAsk(null)}
        >
          <p style={{ margin: 0 }}>
            Its members become ordinary components you can edit freely. The version it came from is recorded.
          </p>
          <p className="muted" style={{ margin: 0 }}>
            It will no longer be offered updates when the definition changes. Undo restores the block.
          </p>
        </ConfirmDialog>
      )}
    </div>
    </DisplayUnitsContext.Provider>
  );
}
